import { useRef, useCallback } from 'react';

/**
 * Manages a queue of base64-encoded audio chunks for sequential TTS playback.
 * Uses Web Audio API with HTMLAudioElement fallback.
 *
 * @param {Object} options
 * @param {Function} options.onQueueEmpty - Called when queue is drained and stream is done
 * @param {Function} options.onPlaying - Called when playback starts (receives 'speaking')
 */
export default function useAudioPlaybackQueue({ onQueueEmpty, onPlaying } = {}) {
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const streamDoneRef = useRef(false);
  const ttsAudioRef = useRef(null);
  const ttsAudioContextRef = useRef(null);

  const playNextInQueue = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      if (streamDoneRef.current) {
        onQueueEmpty?.();
      }
      return;
    }
    isPlayingRef.current = true;
    onPlaying?.();
    const nextAudio = audioQueueRef.current.shift();

    const playWithWebAudio = async () => {
      try {
        if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
          ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const audioContext = ttsAudioContextRef.current;
        if (audioContext.state === 'suspended') await audioContext.resume();
        const binaryString = atob(nextAudio.audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        const audioBuffer = await audioContext.decodeAudioData(bytes.buffer.slice(0));
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);
        source.onended = () => { ttsAudioRef.current = null; playNextInQueue(); };
        source.start(0);
        ttsAudioRef.current = { source, context: audioContext };
      } catch (e) {
        const audioSrc = `data:audio/mp3;base64,${nextAudio.audio}`;
        const audio = new Audio(audioSrc);
        ttsAudioRef.current = audio;
        audio.onended = () => { ttsAudioRef.current = null; playNextInQueue(); };
        audio.onerror = () => { ttsAudioRef.current = null; playNextInQueue(); };
        audio.play().catch(() => { ttsAudioRef.current = null; playNextInQueue(); });
      }
    };
    playWithWebAudio();
  }, [onQueueEmpty, onPlaying]);

  const enqueueAudio = useCallback((audioData) => {
    audioQueueRef.current.push(audioData);
    if (!isPlayingRef.current) playNextInQueue();
  }, [playNextInQueue]);

  const markStreamDone = useCallback(() => {
    streamDoneRef.current = true;
    if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
      onQueueEmpty?.();
    }
  }, [onQueueEmpty]);

  const stopPlayback = useCallback(() => {
    audioQueueRef.current = [];
    streamDoneRef.current = false;
    isPlayingRef.current = false;
    if (ttsAudioRef.current) {
      try {
        if (ttsAudioRef.current.source) {
          ttsAudioRef.current.source.onended = null;  // prevent the stop() from re-triggering the queue
          ttsAudioRef.current.source.stop();
        } else if (ttsAudioRef.current.pause) {
          ttsAudioRef.current.pause();
        }
      } catch (e) {}
      ttsAudioRef.current = null;
    }
    // Close the TTS audio context too — a full hard-cut. playNextInQueue()
    // recreates it on demand if it's closed, so this is safe.
    if (ttsAudioContextRef.current) {
      try { ttsAudioContextRef.current.close(); } catch (e) {}
      ttsAudioContextRef.current = null;
    }
  }, []);

  const resetQueue = useCallback(() => {
    audioQueueRef.current = [];
    streamDoneRef.current = false;
    isPlayingRef.current = false;
  }, []);

  return {
    enqueueAudio,
    markStreamDone,
    stopPlayback,
    resetQueue,
    playNextInQueue,
    audioQueueRef,
    isPlayingRef,
    streamDoneRef,
  };
}
